import { ChangePaidMembershipPlanRepository } from '@/models/api/paid-member-price/member-plan/change/repository'
import {
  AvailablePaidMembershipPlanInGroup
} from '@/models/api/paid-member-price/member-plan/group/available-paid-membership-plan-group'
import {
  FindAvailablePaidMembershipPlanGroupRepository,
} from '@/models/api/paid-member-price/member-plan/group/available-paid-membership-plan-group-repository'
import { AppState } from '@spa/components/pages/paid-membership/change/model/state'
import { Either, left, right } from 'fp-ts/lib/Either'
import { fromNullable } from 'fp-ts/lib/Option'

export interface PlanState {
  before: Plan,
  after: Plan
}
type Plan = AvailablePaidMembershipPlanInGroup

export interface Repository {
  find: FindAvailablePaidMembershipPlanGroupRepository
  change: ChangePaidMembershipPlanRepository
}

interface InteractionError { error: Error, appState: AppState }
export class ChangePlanInteraction {
  public static forDowngrade(repo: Repository): ChangePlanInteraction {
    return new ChangePlanInteraction(
      param => repo.change.downgrade(param),
      ({ before, after }) => before.canDowngradeTo(after),
      repo
    )
  }
  public static forUpgrade(repo: Repository): ChangePlanInteraction {
    return new ChangePlanInteraction(
      param => repo.change.upgrade(param),
      ({ before, after }) => before.canUpgradeTo(after),
      repo
    )
  }

  private constructor(
    private readonly action: (param: { before: Plan, after: Plan }) => Promise<void>,
    private readonly validateChangable: (s: PlanState) => boolean,
    private readonly repotiroy: Repository
  ) {}

  async initialize(params: { groupId: string, beforePlanId: string, afterPlanId: string })
    : Promise<Either<InteractionError, { planState: PlanState, appState: AppState }>> {
    try {
      const res = await this.repotiroy.find.findBy(params.groupId)

      const planStateRes = res.chain((group) => {
        const beforeOpt = fromNullable(group.plans.find(p => p.externalId === params.beforePlanId))
        const afterOpt = fromNullable(group.plans.find(p => p.externalId === params.afterPlanId))

        return beforeOpt.chain(
          before => afterOpt.map(
            after => ({ before, after })
          )
        )
      })

      return planStateRes.fold(
        left({ error: new Error(`${res} is not fulfilled`), appState: 'initialize-failed' }),
        (planState) =>  {
          const { before, after } = planState
          if (!before.subscribed()) {
            return right({
              planState,
              appState: 'before-plan-not-subscribed-now',
            })
          }
          if (after.subscribed()) {
            return right({
              planState,
              appState: 'after-plan-already-subscribed',
            })
          }
          if (!this.validateChangable(planState)) {
            return right({
              planState,
              appState: 'change-forbidden',
            })
          }

          return right({
            planState,
            appState: 'initialize-succeed',
          })
        }
      )
    } catch (error) {
      return left({ error, appState: 'initialize-failed' })
    }
  }

  async change(state: PlanState): Promise<Either<InteractionError, AppState>> {
    try {
      await this.action(state)

      return right('change-succeed')
    } catch (error) {
      return left({ error, appState: 'change-failed' })
    }
  }
}

export class StateNotFulfilledError extends Error {}
